import React from "react";
import { ShieldCheck, FlaskConical, Microscope, Truck } from "lucide-react";
import SEO from "../components/SEO";

const pillars = [
  { icon: ShieldCheck, title: "Third-Party Verified", body: "Every lot is independently HPLC and mass-spec tested. Certificates of analysis available on request." },
  { icon: FlaskConical, title: "Lyophilized Stability", body: "Compounds are freeze-dried for maximum stability and shipped in research-grade vials." },
  { icon: Microscope, title: "Research-Only", body: "We supply qualified researchers exclusively. Products are not for human or animal consumption." },
  { icon: Truck, title: "Cold-Chain Logistics", body: "Discreet, temperature-controlled shipping with 24-hour dispatch on all in-stock SKUs." },
];

export default function About() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-20">
      <SEO
        title="About ProSource Labs — Research-Grade Peptide Supplier"
        description="ProSource Labs is a US-based direct-to-researcher retail store for HPLC-verified research peptides — not a wholesaler. Buy a single vial, no minimum. Third-party lab verification, lyophilized stability, cold-chain logistics, and domestic shipping."
        path="/about"
      />
      <div className="overline">ABOUT</div>
      <h1 className="font-heading text-5xl sm:text-6xl font-light leading-tight mt-4 max-w-3xl">
        Built for the bench.<br />
        <span className="text-[#00C3FF]">Engineered for trust.</span>
      </h1>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-16">
        <div className="lg:col-span-2 space-y-6 text-[#8A94A6] leading-relaxed">
          <p>
            ProSource Labs was founded by researchers, for researchers. We believe access to consistent,
            pharmaceutical-grade peptides should not require navigating a maze of unverified suppliers.
            Every compound we sell is sourced from qualified manufacturers, batch-tested for identity
            and purity, and dispatched from a controlled facility.
          </p>
          <p>
            Our catalog focuses on the modern peptide research landscape — from metabolic GLP-1 analogs
            to growth-hormone secretagogues, healing peptides, and longevity research compounds. We do
            not sell anything we would not use in our own laboratories.
          </p>
          <p>
            Transparency is non-negotiable. Certificates of analysis, third-party testing reports, and
            supply-chain documentation are available on request to verified institutional accounts.
          </p>
        </div>
        <div className="surface p-8">
          <div className="overline mb-3">MISSION</div>
          <p className="font-heading text-2xl leading-tight">
            Precision in every milligram. Purity beyond the spec sheet. Performance the bench can rely on.
          </p>
        </div>
      </div>

      <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {pillars.map((p) => (
          <div key={p.title} className="bg-[#0C0C12] p-8 space-y-4">
            <p.icon className="text-[#00C3FF]" size={22} />
            <div className="font-heading text-lg">{p.title}</div>
            <p className="text-sm text-[#8A94A6] leading-relaxed">{p.body}</p>
          </div>
        ))}
      </div>

      {/* ----- Why Domestic ----- */}
      <div className="mt-24 border-t border-white/10 pt-20" data-testid="about-why-domestic">
        <div className="overline">WHY DOMESTIC</div>
        <h2 className="font-heading text-4xl sm:text-5xl font-light leading-tight mt-3 max-w-3xl">
          Why Order Domestic with <span className="text-[#00C3FF]">ProSource Labs?</span>
        </h2>
        <p className="font-heading text-xl text-[#8A94A6] mt-4 max-w-2xl">
          Faster Shipping. Lower Risk. Better Quality.
        </p>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-6 text-[#8A94A6] leading-relaxed">
            <p>
              Unlike overseas grey-market suppliers, ProSource Labs ships directly from Southern California
              with typical delivery times of just <span className="text-white">2–3 business days</span>.
              International peptide shipments can take <span className="text-white">3–6 weeks</span>, with
              the constant risk of customs delays, confiscation, or seizure — if the package even arrives at all.
            </p>
          </div>
          <div className="surface p-6">
            <div className="overline text-[#00C3FF] mb-3">NO CUSTOMS RISK</div>
            <ul className="space-y-2 text-sm text-[#8A94A6]">
              <li>· No international customs inspections</li>
              <li>· No overseas import complications</li>
              <li>· No waiting weeks for uncertain tracking</li>
              <li>· Reliable U.S.-based shipping &amp; support</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="surface p-8 space-y-4">
            <div className="overline text-[#00C3FF]">WHY DOMESTIC COSTS MORE</div>
            <h3 className="font-heading text-2xl leading-tight">And why that&apos;s a good thing.</h3>
            <p className="text-sm text-[#8A94A6] leading-relaxed">
              Yes, domestic inventory costs more than direct-from-China grey-market sources. That added cost reflects:
            </p>
            <ul className="space-y-2 text-sm text-[#8A94A6]">
              <li>· U.S.-based warehousing and fulfillment</li>
              <li>· Faster delivery logistics</li>
              <li>· Higher quality-control standards</li>
              <li>· Reduced contamination and handling risk</li>
              <li>· Consistent sourcing and inventory management</li>
            </ul>
          </div>
          <div className="surface p-8 space-y-4">
            <div className="overline text-[#00C3FF]">TESTING &amp; QUALITY CONTROL</div>
            <h3 className="font-heading text-2xl leading-tight">It actually matters.</h3>
            <p className="text-sm text-[#8A94A6] leading-relaxed">
              At ProSource Labs, products undergo additional handling, storage, and verification before
              reaching customers — meaning:
            </p>
            <ul className="space-y-2 text-sm text-[#8A94A6]">
              <li>· Better batch consistency</li>
              <li>· Reduced risk of degraded products</li>
              <li>· Improved packaging and cold-chain handling</li>
              <li>· Greater confidence in what you receive</li>
            </ul>
            <p className="text-xs text-[#5C657A] leading-relaxed pt-2 border-t border-white/5">
              With overseas suppliers, lower prices often come with unknown storage conditions, inconsistent
              testing standards, and little accountability once the shipment leaves the country.
            </p>
          </div>
        </div>

        <div className="mt-16 surface p-8 sm:p-10 border-l-4 border-l-[#00C3FF]">
          <div className="overline text-[#00C3FF]">THE BOTTOM LINE</div>
          <h3 className="font-heading text-3xl leading-tight mt-3">
            With ProSource Labs, you&apos;re paying for —
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6 text-sm">
            {["Speed", "Reliability", "Reduced risk", "Domestic fulfillment", "Better quality assurance", "Peace of mind"].map((v) => (
              <div key={v} className="border border-white/10 px-4 py-3 font-mono-tech uppercase tracking-wider text-xs text-white">
                {v}
              </div>
            ))}
          </div>
          <p className="text-[#8A94A6] mt-6 leading-relaxed">
            When consistency and dependable delivery matter, domestic sourcing is the advantage.
          </p>
          <p className="overline text-[#00C3FF] mt-8">— ProSource Labs</p>
        </div>
      </div>
    </div>
  );
}
